import { gql } from '@apollo/client';
import {
  Box,
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select, { OptionProps, SingleValue } from 'react-select';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigPowerScheduleSelect_ConfigFragment,
  ConfigPowerScheduleSelect_CustomerFragment,
  useConfigUpdatePowerScheduleMutation,
} from './__generated__/ConfigDisplayPowerSchedule.graphql';

const powerScheduleOption = selectOptionBaseSchema;

type PowerScheduleOption = z.TypeOf<typeof powerScheduleOption>;

const schema = z.object({
  configId: z.string(),
  powerSchedule: powerScheduleOption.optional().nullable(),
});

type FormValues = z.TypeOf<typeof schema>;

export function ConfigPowerScheduleSelect({
  customer,
  config,
  configStatus,
}: {
  customer: ConfigPowerScheduleSelect_CustomerFragment;
  config: ConfigPowerScheduleSelect_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}) {
  const options = useMemo<PowerScheduleOption[]>(
    () =>
      customer.powerSchedules.map<PowerScheduleOption>((p) => ({
        label: p.title,
        value: p.id,
      })),
    [customer],
  );

  const toast = useToast();
  const currentPowerSchedule = useMemo(
    () => options.find((o) => o.value === config.powerSchedule?.schedule?.id) || null,
    [config, options],
  );

  const {
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      configId: config.id,
      powerSchedule: currentPowerSchedule,
    },
    resolver: zodResolver(schema),
  });

  const [selectedSchedule, setSelectedSchedule] =
    useState<SingleValue<PowerScheduleOption>>(currentPowerSchedule);

  const [updatePowerSchedule, { loading }] = useConfigUpdatePowerScheduleMutation();

  useEffect(() => {
    setSelectedSchedule(currentPowerSchedule);
  }, [currentPowerSchedule]);

  const handleChange = async (selected: PowerScheduleOption | null) => {
    try {
      await updatePowerSchedule({
        variables: {
          input: {
            configurationId: config.id,
            powerScheduleId: selected ? selected.value : null,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorUpdatingPowerSchedule'),
      });
    }
  };

  const { verifyUserPermissions } = useAuth();

  const isDisplaySettingsUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);

  return (
    <GridItem
      colSpan={{
        base: 8,
        sm: 6,
        lg: 8,
        xl: 5,
      }}
    >
      <FormControl isInvalid={Boolean(errors.powerSchedule)}>
        <FormLabel w={'100%'}>
          {t('selectSchedule')}
          <FormLabelPendingIndicator isPending={loading} />
        </FormLabel>
        <Controller
          name="powerSchedule"
          control={control}
          render={({ field }) => (
            <Select
              value={selectedSchedule}
              options={options}
              components={{
                ...components,
                Option: ManagePowerScheduleSelectOption,
              }}
              onChange={(selected) => {
                field.onChange(selected);
                handleChange(selected);
              }}
              isLoading={isSubmitting}
              placeholder={t('select')}
              isMulti={false}
              escapeClearsValue={false}
              isClearable={true}
              isSearchable={true}
              menuPlacement="auto"
              isDisabled={isDisplaySettingsUpdateDisabled}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="powerSchedule"
          render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
        />
      </FormControl>
    </GridItem>
  );
}

export function ManagePowerScheduleSelectOption({
  isSelected,
  innerProps,
  ...rest
}: OptionProps<PowerScheduleOption, false>) {
  const data = rest.data;

  return (
    <Box
      display="flex"
      alignItems="center"
      paddingX="3.5"
      paddingY="2"
      cursor="pointer"
      color={isSelected ? 'white' : 'blue.800'}
      background={isSelected ? 'blue.400' : 'white'}
      fontSize="md"
      _hover={{
        background: isSelected ? 'blue.400' : 'blue.50',
      }}
      {...innerProps}
    >
      <chakra.span display="inline-block" flex="1">
        {data.label}
      </chakra.span>
    </Box>
  );
}

ConfigPowerScheduleSelect.graphql = {
  fragments: {
    ConfigPowerScheduleSelect_customer: gql`
      fragment ConfigPowerScheduleSelect_customer on Customer {
        id
        powerSchedules {
          id
          title
        }
      }
    `,
    ConfigPowerScheduleSelect_config: gql`
      fragment ConfigPowerScheduleSelect_config on ConfigurationModel {
        id
        powerSchedule {
          schedule {
            id
            title
            timeBlocks {
              start
              end
              day
            }
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdatePowerSchedule: gql`
      mutation ConfigUpdatePowerSchedule($input: ConfigurationUpdatePowerScheduleInput!) {
        configurationUpdatePowerSchedule(input: $input) {
          id
        }
      }
    `,
  },
};
